import { DeleteOutlined, EditOutlined, PlusOutlined } from "@ant-design/icons";
import { SearchBar, ISearchQuery } from "@components";
import {
  Api,
  canDeleteContent,
  canManageContentActivation,
  hasPermission,
  IWordCard,
  PERMISSIONS,
} from "@model";
import { Button, Checkbox, Table, message, Modal } from "antd";
import { ColumnsType } from "antd/lib/table";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router";
import { useSearchParams } from "react-router-dom";
import { wordCardListTitle, wordCardWordSummary } from "./wordCardLanguages";

const WordCardListPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [cards, setCards] = useState<IWordCard[]>([]);
  const [loading, setLoading] = useState(true);
  const [pageSize, setPageSize] = useState(100);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [sortField, setSortField] = useState<string | undefined>();
  const [sortOrder, setSortOrder] = useState<string | undefined>();
  const [searchQuery, setSearchQuery] = useState<ISearchQuery>({
    q: searchParams.get("q") ?? "",
  });
  const navigate = useNavigate();
  const canEdit = hasPermission(PERMISSIONS.wordCardsEdit);
  const canCreate = hasPermission(PERMISSIONS.wordCardsCreate);
  const canActivate = canManageContentActivation();

  const syncStateFromUrl = useCallback(() => {
    const q = searchParams.get("q") ?? "";
    setSearchQuery((prev) => (prev.q !== q ? { q } : prev));
    setCurrentPage(Math.max(1, Number(searchParams.get("page")) || 1));
    const ps = Number(searchParams.get("pageSize"));
    if (ps) setPageSize(Math.min(100, Math.max(10, ps)));
    setSortField(searchParams.get("sortField") || undefined);
    setSortOrder(searchParams.get("sortOrder") || undefined);
  }, [searchParams]);

  useEffect(() => {
    syncStateFromUrl();
  }, [syncStateFromUrl]);

  const fetchCards = useCallback(() => {
    setLoading(true);
    Api.get("/api/admin/panel/word-cards", {
      params: {
        title: searchQuery.q,
        sortField,
        sortOrder,
        page: currentPage,
        limit: pageSize,
      },
    })
      .then((response) => {
        const data = response.data.data ?? [];
        setCards(data);
        setTotalPages(data.length < pageSize ? currentPage : currentPage + 1);
      })
      .catch(() => message.error("Failed to fetch word cards"))
      .finally(() => setLoading(false));
  }, [searchQuery.q, sortField, sortOrder, currentPage, pageSize]);

  useEffect(() => {
    fetchCards();
  }, [fetchCards]);

  const updateUrl = (updates: Record<string, string | undefined>) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([key, value]) => {
      if (value) next.set(key, value);
      else next.delete(key);
    });
    setSearchParams(next, { replace: true });
  };

  const returnTo = searchParams.toString()
    ? `/panel/word-cards?${searchParams.toString()}`
    : "/panel/word-cards";

  const updateActivation = useCallback(async (record: IWordCard, isActive: boolean) => {
    await Api.patch(`/api/admin/panel/word-cards/${record._id}/activation`, {
      isActive,
    });
    setCards((current) =>
      current.map((item) =>
        item._id === record._id ? { ...item, isActive } : item,
      ),
    );
  }, []);

  const handleDelete = useCallback(
    (record: IWordCard) => {
      Modal.confirm({
        title: "Delete word card",
        content: `Delete "${wordCardListTitle(record)}"?`,
        okText: "Delete",
        okType: "danger",
        onOk: async () => {
          try {
            await Api.delete(`/api/admin/panel/word-cards/${record._id}`);
            message.success("Word card deleted");
            fetchCards();
          } catch {
            message.error("Failed to delete word card");
          }
        },
      });
    },
    [fetchCards],
  );

  const columns = useMemo(
    (): ColumnsType<IWordCard> => [
      {
        title: "Active",
        width: 70,
        dataIndex: "isActive",
        render: (isActive: boolean, record) => (
          <Checkbox
            checked={isActive === true}
            disabled={!canActivate}
            onChange={(event) =>
              void updateActivation(record, event.target.checked)
            }
          />
        ),
      },
      {
        title: "Title",
        dataIndex: "title",
        render: (_: unknown, record) =>
          canEdit ? (
            <a
              href={`/panel/word-cards/create/${record._id ?? ""}`}
              onClick={(event) => {
                event.preventDefault();
                navigate(`/panel/word-cards/create/${record._id ?? ""}`, {
                  state: { returnTo },
                });
              }}
            >
              {wordCardListTitle(record)}
            </a>
          ) : (
            wordCardListTitle(record)
          ),
      },
      { title: "Topic", dataIndex: "topic", width: 160 },
      {
        title: "Words",
        width: 180,
        render: (_: unknown, record) => wordCardWordSummary(record),
      },
      {
        title: "Creator",
        width: 140,
        render: (_: unknown, record) => record.creator?.name ?? "-",
      },
      {
        title: "Actions",
        width: 160,
        render: (_: unknown, record) => (
          <div style={{ display: "flex", gap: 8 }}>
            {canEdit && (
              <Button
                type="link"
                icon={<EditOutlined />}
                onClick={() =>
                  navigate(`/panel/word-cards/create/${record._id}`, {
                    state: { returnTo },
                  })
                }
              >
                Edit
              </Button>
            )}
            {canDeleteContent(
              record.creator,
              PERMISSIONS.wordCardsDelete,
              PERMISSIONS.wordCardsDeleteOwn,
            ) && (
              <Button
                type="link"
                danger
                icon={<DeleteOutlined />}
                onClick={() => handleDelete(record)}
              >
                Delete
              </Button>
            )}
          </div>
        ),
      },
    ],
    [canActivate, canEdit, handleDelete, navigate, returnTo, updateActivation],
  );

  return (
    <div>
      <div className="flex justify-space-between">
        <SearchBar
          style={{ flex: "1", minWidth: 200, margin: "3px 0" }}
          value={searchQuery.q}
          onChange={(event) => {
            setSearchQuery(event);
            setCurrentPage(1);
            updateUrl({ q: event.q || undefined, page: "1" });
          }}
          searchType="Enter"
        />
        {canCreate && (
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={() =>
              navigate("/panel/word-cards/create", { state: { returnTo } })
            }
          >
            New
          </Button>
        )}
      </div>
      <Table
        loading={loading}
        size="small"
        rowKey={(record) => record._id ?? ""}
        columns={columns}
        dataSource={cards}
        pagination={{
          current: currentPage,
          pageSize,
          total: totalPages * pageSize,
          onChange: (page, nextSize) => {
            setCurrentPage(page);
            if (nextSize) setPageSize(nextSize);
            updateUrl({
              page: String(page),
              ...(nextSize ? { pageSize: String(nextSize) } : {}),
            });
          },
        }}
        onChange={(_pagination, _filters, sorter) => {
          if (Array.isArray(sorter) || sorter?.field == null) return;
          const sf = String(sorter.field);
          const so = sorter.order === "ascend" ? "asc" : "desc";
          setSortField(sf);
          setSortOrder(so);
          updateUrl({ sortField: sf, sortOrder: so });
        }}
      />
    </div>
  );
};

export default WordCardListPage;
